'use client'

import { useMemo, useRef } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { TransformStep } from '@/lib/commands/graph-serialize'
import { TemplateChipBar } from '../TemplateChipBar'

export function TransformStepEditor({
  step,
  priorStepIds,
  priorVars,
  onChange,
}: {
  step: TransformStep
  priorStepIds: string[]
  priorVars: string[]
  onChange: (step: TransformStep) => void
}) {
  const entries = Object.entries(step.assign)

  const addRow = () => {
    let index = entries.length + 1
    let key = `value_${index}`
    while (step.assign[key] !== undefined) {
      index += 1
      key = `value_${index}`
    }
    onChange({ ...step, assign: { ...step.assign, [key]: '' } })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Label>Assignments</Label>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <HugeiconsIcon icon={PlusSignIcon} className="size-4" />
          Add row
        </Button>
      </div>

      {entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-atrium-border bg-atrium-content p-4 text-sm text-atrium-black/60">
          Add a variable to make it available as <span className="font-mono">${'{vars.name}'}</span> in later steps.
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map(([key, value]) => (
            <AssignmentRow
              key={key}
              assignKey={key}
              value={value}
              priorStepIds={priorStepIds}
              priorVars={priorVars}
              onKeyChange={(nextKey) => {
                const cleanedKey = nextKey.replace(/[^a-zA-Z0-9_]/g, '')
                const nextAssign = { ...step.assign }
                delete nextAssign[key]
                nextAssign[cleanedKey] = value
                onChange({ ...step, assign: nextAssign })
              }}
              onValueChange={(nextValue) => onChange({ ...step, assign: { ...step.assign, [key]: nextValue } })}
              onRemove={() => {
                const nextAssign = { ...step.assign }
                delete nextAssign[key]
                onChange({ ...step, assign: nextAssign })
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AssignmentRow({
  assignKey,
  value,
  priorStepIds,
  priorVars,
  onKeyChange,
  onValueChange,
  onRemove,
}: {
  assignKey: string
  value: string
  priorStepIds: string[]
  priorVars: string[]
  onKeyChange: (key: string) => void
  onValueChange: (value: string) => void
  onRemove: () => void
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const vars = useMemo(() => priorVars.filter((key) => key !== assignKey), [assignKey, priorVars])

  const insertToken = (token: string) => {
    const textarea = textareaRef.current
    if (!textarea) {
      onValueChange(`${value}${token}`)
      return
    }
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    onValueChange(`${value.slice(0, start)}${token}${value.slice(end)}`)
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <div className="rounded-lg border border-atrium-border bg-white p-3">
      <div className="grid gap-3 sm:grid-cols-[160px_1fr_auto]">
        <div className="space-y-2">
          <Label>Key</Label>
          <Input value={assignKey} onChange={(event) => onKeyChange(event.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Value</Label>
          <TemplateChipBar stepIds={priorStepIds} vars={vars} onInsert={insertToken} />
          <Textarea ref={textareaRef} value={value} onChange={(event) => onValueChange(event.target.value)} />
        </div>
        <Button type="button" variant="ghost" size="icon" className="self-end" onClick={onRemove} aria-label="Remove assignment">
          <HugeiconsIcon icon={Delete02Icon} className="size-4" />
        </Button>
      </div>
    </div>
  )
}
